import React, { useEffect } from 'react';
import { registerAsWebComponent } from 'react-webcomponentify';
import BonnetWebComponent from '@bonnet/webcomponent';
import { BrowserAgent } from '@newrelic/browser-agent';

import {
    ownerEmailClick,
    ndsCalcInteraction,
    ndsInviewImpression,
    ndsPencilingEvents,
    ndsNavigationClick,
    ndsOfferBreakDownClick,
    ndsKBBTradeInCTAClick,
    ndsEsntialBuyOnlineClick,
    ndsEligibleImpression,
    ndsStartCalculatorImpression,
    ndsVerifyCreditCTAClick,
    ndsInitialClick,
    ndsComponentLoad,
    myWalletInputChange,
} from 'reaxl-analytics';

import { dealsDuck, inventoryDuck, ownersDuck, paymentsDuck, resultsDuck } from '@syndicated-wallet/ducks';

import { getFeatureFlags } from '@syndicated-wallet/helpers';
import { logger, setIsolatedNewRelicAgent, trackPageAction } from '@atc/syndicated-logging';
import dataLayerMock from '../mocks/dataLayer.mock.js';
import flags from '../constants/flags';

import UnifiedDealStarter from './UnifiedDealStarter';
import getStylesheets from '../themes/getStylesheets';

logger.configure({ level: logger.logLevel.DEBUG });
const udsLogger = logger.getLogger('UDS');

const analytics = {
    dataLayer: () => dataLayerMock,
    getDataIsland: () => ({
        page: {
            BIRF: {
                pg: 'test',
                pg_id: 'test',
            },
        },
    }),
    value: {
        ownerEmailClick,
        ndsCalcInteraction,
        ndsInviewImpression,
        ndsPencilingEvents,
        ndsNavigationClick,
        ndsOfferBreakDownClick,
        ndsKBBTradeInCTAClick,
        ndsEsntialBuyOnlineClick,
        ndsEligibleImpression,
        ndsStartCalculatorImpression,
        ndsVerifyCreditCTAClick,
        ndsInitialClick,
        ndsComponentLoad,
        myWalletInputChange,
    },
    option: {
        isOptimizelyEdge: false,
        disableGTM: false,
        disableGTMEvents: false,
        customBrand: 'myWallet', // keep custom brand
        disablePixall: false,
        disableAdobe: true,
        useCDL: true,
    },
};

function loadNewRelicScript(shadowRoot, callback) {
    if (shadowRoot.querySelector('script[id="newrelic_uds"]')) {
        udsLogger.debug('New Relic script already loaded in this component - skipping callback');
        return;
    }

    const script = document.createElement('script');
    script.id = 'newrelic_uds';
    script.type = 'text/javascript';
    script.src = process.env.NR_SCRIPT_PATH;
    script.async = true;
    script.onload = () => {
        udsLogger.debug('New Relic script loaded successfully.');
        if (callback) {
            udsLogger.debug('Executing callback...');
            callback();
        }
    };

    script.onerror = (error) => {
        udsLogger.debug('Failed to load New Relic script:', error);
    };

    try {
        shadowRoot.appendChild(script);
    } catch (error) {
        udsLogger.debug('Error appending script:', error);
    }
}

export default function UnifiedDealStarterWebComponent(props = {}) {
    const { 'data-feature-flags': dataFeatureFlags = '', theme, ...rest } = props;
    const allFeatureFlags = getFeatureFlags({ ...flags }, dataFeatureFlags);
    const reducers = [dealsDuck, inventoryDuck, ownersDuck, paymentsDuck, resultsDuck];
    const iconFontPath = [process.env.S3_WWW_PATH, 'assets', 'fonts'].filter(Boolean).join('/');
    const themePath = [process.env.S3_WWW_PATH, 'assets'].filter(Boolean).join('/');

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const enablePerformanceObserver = urlParams.get('enablePerfDebug') === 'true';
        const MAX_RETRIES = 10;
        let retryCount = 0;

        const initNewRelic = () => {
            const webComponent = document.querySelector('unified-deal-starter');

            if (!webComponent || !webComponent.shadowRoot) {
                retryCount += 1;
                if (retryCount >= MAX_RETRIES) {
                    udsLogger.debug(`Failed to initialize New Relic after ${MAX_RETRIES} attempts`);
                    return;
                }
                udsLogger.debug('Web component or shadow root not ready, retrying...');
                setTimeout(initNewRelic, 100);
                return;
            }

            loadNewRelicScript(webComponent.shadowRoot, () => {
                const newRelicAgent = new BrowserAgent({
                    isolatedAgent: true,
                    componentName: 'UnifiedDealStarter',
                });
                newRelicAgent.start();

                setIsolatedNewRelicAgent(newRelicAgent);

                trackPageAction('ComponentLoaded', {
                    component: 'UnifiedDealStarter',
                });
            });
        };

        initNewRelic();

        if (enablePerformanceObserver && window.PerformanceObserver) {
            const observer = new PerformanceObserver((list) => {
                list.getEntries().forEach((entry) => {
                    udsLogger.debug('Performance Entry:', entry);
                });
            });

            observer.observe({
                entryTypes: ['mark', 'measure', 'navigation', 'payment-rendered', 'config-init'],
            });

            return () => {
                observer.disconnect();
            };
        }

        return () => {};
    }, []);

    return (
        <BonnetWebComponent
            iconFontPath={iconFontPath}
            analytics={analytics}
            reducers={reducers}
            stylesheets={getStylesheets({ theme, themePath })}
            flags={{
                config: allFeatureFlags,
            }}
        >
            <UnifiedDealStarter {...rest} />
        </BonnetWebComponent>
    );
}

registerAsWebComponent(UnifiedDealStarterWebComponent, 'unified-deal-starter', 'open');
